import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { size } from 'lodash';
import apolloClient from '@src/graphql/apollo';
import Checkbox from '@src/components/Checkbox';
import { Button } from '@src/components/Button';
import { TextField } from '@src/components/TextField';
import { OpeningDeactivatedModal } from '@src/containers/SubscriptionModals';
import { capitalize } from '@src/util/capitalize';
import { getApplicants } from '@src/redux/selectors/applicants';
import { closeModal, setModalContent } from '@src/redux/actions/modal';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { trackEvent } from '@src/metrics';
import { OpeningSourceEnum, StopOpeningDocument, PauseOpeningDocument } from '@src/graphql/generated';
import Rating from './Rating';
import './styles.scss';

const FEEDBACK_LIST = {
  enoughCandidates: 'We received enough candidates to continue with',
  filledThroughMeetFrank: 'We filled the position through MeetFrank',
  filledThroughOther: 'We filled the position through other channels',
  irrelevantCandidates: 'We received too many irrelevant candidates',
  muchWorkload: 'There was too much workload',
  notEnoughCandidates: 'We didn’t receive enough candidates',
  onHold: 'The vacancy was put on hold',
  other: 'Other',
};

const TYPE_DATA = {
  pause: {
    button: 'Pause Offer',
    header: 'Pause offer',
    info: (
      <Fragment>
        Once you pause the opening, new applicants will not be able to connect with you.
        <br />
        You can still chat with already interested applicants.
        <br />
        <br />
        <span>Please give us feedback. I’m pausing because:</span>
      </Fragment>
    ),
  },
  stop: {
    button: 'Stop Offer',
    header: 'Stop offer',
    info: (
      <Fragment>
        Once you stop the opening, new applicants will not be able to connect with you
        <br />
        and the position is no longer available for already interested applicants.
        <br />
        <br />
        <span>Please give us feedback. I’m stopping because:</span>
      </Fragment>
    ),
  },
};

const VIEW = {
  rating: 'rating',
  reason: 'reason',
};

@connect(
  (state, props) => ({
    applicantsLength: size(getApplicants(state, props.openingId)),
  }),
  {
    closeModal,
    setModalContent,
  },
)
export default class ActionModal extends PureComponent {
  static propTypes = {
    applicantsLength: PropTypes.number.isRequired,
    closeModal: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    openingId: PropTypes.string.isRequired,
    position: PropTypes.string.isRequired,
    setModalContent: PropTypes.func.isRequired,
    type: PropTypes.oneOf(['stop', 'pause']).isRequired,
  };

  state = {
    activeView: VIEW.rating,
    comment: '',
    rating: null,
    selected: undefined,
  };

  inputRef = React.createRef();

  renderRatingView = () => {
    const { openingId } = this.props;

    return (
      <Rating
        onNumberClick={rating => {
          setTimeout(() => {
            this.setState({ activeView: VIEW.reason, rating }, () =>
              trackEvent({
                context: MetricContext.DEACTIVATE_OPENING,
                interaction: MetricAction.OPEN_DEACTIVATE_REASON_SURVEY,
                payload: { 'Opening Id': openingId },
              }),
            );
          }, 600);

          trackEvent({
            context: MetricContext.DEACTIVATE_OPENING,
            interaction: MetricAction.SELECT_SATISFACTION,
            payload: {
              'Opening Id': openingId,
              'Satisfaction': rating,
            },
          });
        }}
        position={this.props.position}
      />
    );
  };

  renderReasonView = () => {
    const { type, setModalContent, closeModal, openingId, onSuccess, onError, applicantsLength } = this.props;
    const { selected, comment, rating } = this.state;
    const data = TYPE_DATA[type];

    // disable button when nothing is checked or "other" option is checked and comment field is empty
    const btnDisabled = selected === undefined || (FEEDBACK_LIST[selected] === FEEDBACK_LIST.other && !comment.length);

    return (
      <Fragment>
        <h4>{data.header}</h4>

        <div className="funnel-overview-action-modal-info">{data.info}</div>

        <ul className="funnel-overview-action-modal-list">
          {Object.keys(FEEDBACK_LIST).map(key => (
            <li key={key}>
              <Checkbox
                disableStateChange
                label={FEEDBACK_LIST[key]}
                onChange={() => {
                  this.setState({ selected: key });

                  if (FEEDBACK_LIST[key] === FEEDBACK_LIST.other) {
                    // automatically focus input field when "other" option is checked
                    this.inputRef.current.focus();
                  }

                  trackEvent({
                    context: MetricContext.DEACTIVATE_OPENING,
                    interaction: MetricAction.SELECT_REASON,
                    payload: {
                      'Opening Id': openingId,
                      'Reason': FEEDBACK_LIST[key],
                    },
                  });
                }}
                value={key === selected}
              />
            </li>
          ))}
        </ul>

        <TextField
          defaultValue={comment}
          fullWidth
          inputRef={this.inputRef}
          label="Additional comments"
          multiline
          onBlur={e =>
            trackEvent({
              context: MetricContext.DEACTIVATE_OPENING,
              interaction: MetricAction.ADD_COMMENT,
              payload: {
                'Comment': e.target.value,
                'Opening Id': openingId,
              },
            })
          }
          onChange={comment => this.setState({ comment })}
          wrapperClassName="funnel-overview-action-comments"
        />

        <Button
          className="funnel-overview-action-modal-info__action-button"
          isDisabled={btnDisabled}
          onClick={async () => {
            try {
              const opening =
                type === 'stop'
                  ? (
                      await apolloClient.mutate({
                        fetchPolicy: 'no-cache',
                        mutation: StopOpeningDocument,
                        variables: {
                          openingId: openingId,
                          stopOpeningInput: {
                            comment: comment,
                            rating: rating,
                            reason: FEEDBACK_LIST[selected],
                          },
                        },
                      })
                    ).data.stopOpening
                  : (
                      await apolloClient.mutate({
                        fetchPolicy: 'no-cache',
                        mutation: PauseOpeningDocument,
                        variables: {
                          openingId: openingId,
                          pauseOpeningInput: {
                            comment: comment,
                            rating: rating,
                            reason: FEEDBACK_LIST[selected],
                          },
                        },
                      })
                    ).data.pauseOpening;

              onSuccess(opening);
              closeModal();

              if (opening.source.type === OpeningSourceEnum.MEETFRANK) {
                setModalContent({
                  content: <OpeningDeactivatedModal />,
                });
              }

              trackEvent({
                context: MetricContext.DEACTIVATE_OPENING,
                interaction: MetricAction.OPEN_DEACTIVATE_OPENING,
                payload: {
                  'Applicant Number': applicantsLength,
                  'Deactivation Type': capitalize(type),
                  'Opening Id': openingId,
                },
              });
            } catch (error) {
              onError(error);
              closeModal();
            }
          }}
          theme={type === 'pause' ? 'blue' : 'red'}
        >
          {data.button}
        </Button>
      </Fragment>
    );
  };

  renderActiveView = active => {
    if (active === VIEW.rating) {
      return this.renderRatingView();
    } else if (active === VIEW.reason) {
      return this.renderReasonView();
    }

    return null;
  };

  render() {
    const { activeView } = this.state;

    return <div className="funnel-overview-action-modal">{this.renderActiveView(activeView)}</div>;
  }
}
